import * as React from 'react';
import { observer } from 'mobx-react';
import { AutoComplete } from 'components/Common/autoComplete';
import { defaultSearchResultLimit, emptyFunction } from 'common/constants';
import { Tooltip } from '@payscale/design';
import { AssignUserViewStore } from 'nomst/Stores/Configuration/assignUserView.store';
import './assignUserView.scss';

export interface IAssignUserViewProps {
    assignUserViewStore: AssignUserViewStore;
    searchFilter?: (searchResult: ISuggestedUser[]) => ISuggestedUser[];
    readonly?: boolean;
    includeAllUsers: boolean;
    isUserAlreadyAssigned: (email: string) => boolean;
}

const handleGetSelectedUser = (user: ISuggestedUser): string => user.displayName;

export class AssignUserView extends React.Component<IAssignUserViewProps> {
    constructor(props: IAssignUserViewProps) {
        super(props);
        this.handleSearch = this.handleSearch.bind(this);
        this.setFoundUser = this.setFoundUser.bind(this);
        this.clearFoundUser = this.clearFoundUser.bind(this);
        this.getSearchSuggestionText = this.getSearchSuggestionText.bind(this);
        this.assignUser = this.assignUser.bind(this);
        this.saveApprover = this.saveApprover.bind(this);
        this.cancel = this.cancel.bind(this);
    }

    render(): React.ReactNode {
        const { assignUserViewStore } = this.props;
        if (assignUserViewStore.isEmpty) {
            return this.renderEmpty();
        }

        if (assignUserViewStore.openSelection) {
            return this.renderOpenSelection();
        }

        return (
            <td className="nomst-assign-user-view">
                {assignUserViewStore.displayName}<br />
                <span className="nomst-assign-user-view__email">{assignUserViewStore.workEmail}</span>
            </td>
        );
    }

    private renderEmpty(): React.ReactNode {
        const { readonly } = this.props;
        return (
            <td className="nomst-assign-user-view" colSpan={2}>
                <button
                    hidden={readonly}
                    onClick={this.assignUser}
                    className="nomst-assign-user-view__assign-user pxl-link"
                >
                    + Assign User
                </button>
            </td>
        );
    }

    private renderOpenSelection(): React.ReactNode {
        return (
            <td className="nomst-assign-user-view" colSpan={2}>
                <AutoComplete
                    id="userSearch"
                    placeholder={'Search user...'}
                    search={this.handleSearch}
                    resultLimit={defaultSearchResultLimit}
                    onChangeInput={emptyFunction}
                    onSuggestionSelected={this.setFoundUser}
                    onClearSearch={this.clearFoundUser}
                    getSelectedValue={handleGetSelectedUser}
                    getRenderedSuggestionText={this.getSearchSuggestionText}
                    initialValue={''}
                    disableInput={false}
                />
                <button onClick={this.cancel} className="nomst-assign-user-view__cancel pxl-link">Cancel</button>
                <button
                    onClick={this.saveApprover}
                    className="pxl-btn pxl-btn-default nomst-assign-user-view__save"
                    disabled={this.isSaveDisabled()}
                >
                    Save
                </button>
                {this.renderTooltip()}
            </td>
        );
    }

    private renderTooltip(): React.ReactNode {
        const { assignUserViewStore, isUserAlreadyAssigned } = this.props;
        if (!assignUserViewStore.selectedUserFromSuggestion
            || !isUserAlreadyAssigned(assignUserViewStore.selectedUserFromSuggestion.emailAddress)) {
            return null;
        }

        return (
            <Tooltip
                hoverText="User already assigned HR role. Users can only have one HR role per cycle."
                tooltipText=""
                display="icon"
                className="nomst-assign-user-view__tooltip"
                id="nomst-assign-user-view-tooltip"
            />
        );
    }

    private isSaveDisabled(): boolean {
        const { assignUserViewStore, isUserAlreadyAssigned } = this.props;
        return !assignUserViewStore.selectedUserFromSuggestion
            || isUserAlreadyAssigned(assignUserViewStore.selectedUserFromSuggestion.emailAddress);
    }

    private cancel(): void {
        const { assignUserViewStore } = this.props;
        assignUserViewStore.cancel();
    }

    private async saveApprover(): Promise<void> {
        const { assignUserViewStore } = this.props;
        await assignUserViewStore.save();
    }

    private assignUser(): void {
        const { assignUserViewStore } = this.props;
        assignUserViewStore.assignUser();
    }

    private getSearchSuggestionText(user: ISuggestedUser): string {
        const { assignUserViewStore } = this.props;
        return assignUserViewStore.getSearchSuggestionText(user);
    }

    private clearFoundUser(): void {
        const { assignUserViewStore } = this.props;
        assignUserViewStore.clearFoundUser();
    }

    private setFoundUser(user: ISuggestedUser): void {
        const { assignUserViewStore } = this.props;
        assignUserViewStore.setFoundUser(user);
    }

    private async handleSearch(searchText: string, resultLimit: number): Promise<ISuggestedUser[]> {
        const { searchFilter, includeAllUsers, assignUserViewStore } = this.props;
        const users = await assignUserViewStore.searchUsers(searchText, resultLimit, includeAllUsers);
        if (!users) {
            return [];
        }

        return searchFilter ? searchFilter(users) : users;
    }
}

export default observer(AssignUserView);